import { Flex, Modal, Select, Table, Tag } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import { AppliedPtoItem, AppliedPtoStatus, PromoCodeItem } from 'Modules'
import { useEffect, useMemo, useState } from 'react'

import { DEFAULT_PAGE_OPTIONS, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { APPLIED_PTO_STATUS_CONFIG, APPLIED_PTO_STATUS_OPTIONS } from 'constants/promo-code'
import { useAppliedPtos } from 'hooks/promo-code'
import { PAGES } from 'routes'

interface PromoCodeAppliedPtosModalProps {
  open: boolean
  promoCode: PromoCodeItem | null
  onClose: () => void
}

/** Open the admin PTO Detail page in a new tab. */
const openPtoDetail = (ptoId: string) => {
  window.open(`${PAGES.PTO}/${ptoId}`, '_blank', 'noopener,noreferrer')
}

export const PromoCodeAppliedPtosModal = ({
  open,
  promoCode,
  onClose,
}: PromoCodeAppliedPtosModalProps) => {
  const [status, setStatus] = useState<AppliedPtoStatus | undefined>(undefined)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  // Reset filter + pagination each time the popup opens for a (different) code.
  useEffect(() => {
    if (open) {
      setStatus(undefined)
      setPage(1)
      setPageSize(DEFAULT_PAGE_SIZE)
    }
  }, [open, promoCode?.id])

  const { ptos, total, isLoading } = useAppliedPtos(open ? promoCode : null, {
    status,
    page,
    limit: pageSize,
  })

  const columns: ColumnsType<AppliedPtoItem> = useMemo(
    () => [
      {
        title: 'No',
        key: 'no',
        width: 72,
        render: (_value, _record, index) => (page - 1) * pageSize + index + 1,
      },
      {
        title: 'PTO name',
        dataIndex: 'name',
        key: 'name',
        render: (name: string, record) => (
          <button
            type="button"
            className="applied-ptos__pto-link"
            onClick={() => openPtoDetail(record.ptoId)}
          >
            {name}
          </button>
        ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 140,
        render: (value: AppliedPtoStatus) => {
          const config = APPLIED_PTO_STATUS_CONFIG[value]
          return (
            <Tag
              className="applied-ptos__status-tag"
              style={{
                color: config.color,
                backgroundColor: config.background,
                borderColor: 'transparent',
              }}
            >
              {config.label}
            </Tag>
          )
        },
      },
    ],
    [page, pageSize]
  )

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setPage(pagination.current ?? 1)
    setPageSize(pagination.pageSize ?? DEFAULT_PAGE_SIZE)
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={600}
      centered
      footer={null}
      className="applied-ptos"
      title={
        <div className="applied-ptos__title">
          <div className="applied-ptos__title-main">Total PTOs Applied</div>
          <div className="applied-ptos__title-sub">{promoCode?.code}</div>
        </div>
      }
    >
      <Flex justify="space-between" align="center" gap={12} className="applied-ptos__toolbar">
        <div className="applied-ptos__total">
          Total: <strong>{total}</strong> PTO
        </div>
        <Select<AppliedPtoStatus | 'ALL'>
          value={status ?? 'ALL'}
          onChange={(value) => {
            setStatus(value === 'ALL' ? undefined : value)
            setPage(1)
          }}
          options={[{ value: 'ALL', label: 'All Status' }, ...APPLIED_PTO_STATUS_OPTIONS]}
          style={{ width: 180 }}
        />
      </Flex>

      <Table<AppliedPtoItem>
        className="applied-ptos__table"
        columns={columns}
        dataSource={ptos}
        rowKey={(record) => record.id}
        loading={isLoading}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: DEFAULT_PAGE_OPTIONS,
        }}
        onChange={handleTableChange}
      />
    </Modal>
  )
}
